import { useState } from 'react';
import { OverlayScrollArea } from '@odo/shared-ui';
import type {
    ConversationQuickReply,
    CreateQuickReplyBody
} from '../../../services/conversationQuickReplies';
import { apiErrorMessage } from '../../../utils/apiError';
import { QuickReplyFormFields } from './QuickReplyFormFields';
import {
    initialFormValues,
    isFormComplete,
    toRequestBody,
    type QuickReplyFormValues
} from './quickReplyForm';

interface QuickReplyFormViewProps {
    // null creates a new reply.
    reply: ConversationQuickReply | null;
    categories: string[];
    isPending: boolean;
    onSave: (body: CreateQuickReplyBody) => Promise<void>;
    onCancel: () => void;
}

export function QuickReplyFormView({
    reply,
    categories,
    isPending,
    onSave,
    onCancel
}: QuickReplyFormViewProps) {
    const [values, setValues] = useState(() => initialFormValues(reply));
    const [serverError, setServerError] = useState<string | null>(null);

    const setField = <K extends keyof QuickReplyFormValues>(
        field: K,
        value: QuickReplyFormValues[K]
    ) => setValues((prev) => ({ ...prev, [field]: value }));

    const submit = async () => {
        setServerError(null);
        try {
            await onSave(toRequestBody(values));
        } catch (error) {
            setServerError(apiErrorMessage(error, 'Could not save the quick reply.'));
        }
    };

    return (
        <>
            <OverlayScrollArea
                className="flex-1 min-h-0"
                contentClassName="px-6 py-5"
                trackInsetPx={4}
            >
                <QuickReplyFormFields
                    values={values}
                    categories={categories}
                    disabled={isPending}
                    onChange={setField}
                />
                {serverError && (
                    <p role="alert" className="mt-4 text-sm text-red-500 break-words">
                        {serverError}
                    </p>
                )}
            </OverlayScrollArea>
            <div className="flex justify-end items-center gap-3 px-6 py-4 border-t border-gray-200">
                <button
                    type="button"
                    onClick={onCancel}
                    disabled={isPending}
                    className="bg-white text-gray-800 border border-gray-300 px-4 py-2 rounded-lg hover:bg-gray-50 transition-colors font-semibold text-sm disabled:opacity-50"
                >
                    Cancel
                </button>
                <button
                    type="button"
                    onClick={() => void submit()}
                    disabled={!isFormComplete(values) || isPending}
                    className="bg-gray-800 text-white rounded-lg px-4 py-2 text-sm font-semibold hover:bg-gray-900 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                    {isPending ? 'Saving…' : reply ? 'Save' : 'Create'}
                </button>
            </div>
        </>
    );
}
